import React, { type FC } from "react";
import {
  EdgeLabelRenderer,
  BaseEdge,
  Position,
  type Edge,
  type EdgeProps,
  getSmoothStepPath,
} from "@xyflow/react";
import theme from "../../../../theme";

const LABEL_GAP = 6;

/**
 * Sits the label just outside the target end of the edge.
 *
 * Centring it on the path reads well on a single line but the labels collide
 * wherever several edges converge, and a fixed "above the target" offset is only
 * correct in one orientation. Offsetting away from the target card, based on
 * which side its handle is on, keeps the label next to its target and clear of
 * the cards in all four orientations.
 */
const getLabelTransform = (
  targetX: number,
  targetY: number,
  targetPosition: Position
) => {
  switch (targetPosition) {
    case Position.Bottom:
      return `translate(-50%, 0) translate(${targetX}px, ${
        targetY + LABEL_GAP
      }px)`;
    case Position.Left:
      return `translate(-100%, -50%) translate(${
        targetX - LABEL_GAP
      }px, ${targetY}px)`;
    case Position.Right:
      return `translate(0, -50%) translate(${
        targetX + LABEL_GAP
      }px, ${targetY}px)`;
    case Position.Top:
    default:
      return `translate(-50%, -100%) translate(${targetX}px, ${
        targetY - LABEL_GAP
      }px)`;
  }
};

// this is a little helper component to render the actual edge label
function EdgeLabel({ transform, label }: { transform: string; label: string }) {
  return (
    <div
      style={{
        position: "absolute",
        background: theme.colors.gray[30],
        padding: "6px 8px",
        color: "#1A202C",
        fontSize: 13,
        fontWeight: 600,
        transform,
        borderRadius: 8,
      }}
    >
      {label}
    </div>
  );
}

const CustomEdge: FC<
  EdgeProps<Edge<{ startLabel: string; endLabel: string }>>
> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  data,
}) => {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      {/* markerEnd carries the arrowhead, which sits at the target end: the
          related party. The direction therefore reads company >> related party,
          matching the source/target convention on Edge. */}
      {/* style carries the dashed stroke used for ended relationships, so it
          has to reach BaseEdge rather than being dropped here */}
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        {data?.endLabel && (
          <EdgeLabel
            transform={getLabelTransform(targetX, targetY, targetPosition)}
            label={data?.endLabel}
          />
        )}
      </EdgeLabelRenderer>
    </>
  );
};

export default CustomEdge;
